/**
 * Optimized theme switching utility
 * Reduces DOM operations and improves theme switching performance
 */

interface ThemeColors {
  [key: string]: string;
}

interface ThemeConfig {
  light: ThemeColors;
  dark: ThemeColors;
}

const THEME_CONFIG: ThemeConfig = {
  light: {
    '--tv-bg-primary': '#FFFFFF',
    '--tv-bg-secondary': '#F7F9FB',
    '--tv-bg-tertiary': '#EFF2F5',
    '--tv-bg-quaternary': '#E0E3E7',
    '--tv-surface-1': '#F7F9FB',
    '--tv-surface-2': '#EFF2F5',
    '--tv-surface-3': '#E0E3E7',
    '--tv-surface-4': '#D1D4DC',
    '--tv-text-primary': '#000000', // Pure black for maximum contrast
    '--tv-text-secondary': '#1A1A1A', // Very dark for excellent readability
    '--tv-text-tertiary': '#333333', // Dark gray for good contrast
    '--tv-text-disabled': '#C8CCD6',
    // --tv-text-inverse: keep white in both themes (used as ink on saturated accent badges).
    '--tv-text-inverse': '#FFFFFF',
    '--tv-border-primary': '#E0E3E7',
    '--tv-border-secondary': '#EFF2F5',
    '--tv-border-tertiary': '#D1D4DC'
  },
  dark: {
    '--tv-bg-primary': '#131722',
    '--tv-bg-secondary': '#1E222D',
    '--tv-bg-tertiary': '#2A2E39',
    '--tv-bg-quaternary': '#363A45',
    '--tv-surface-1': '#1E222D',
    '--tv-surface-2': '#2A2E39',
    '--tv-surface-3': '#363A45',
    '--tv-surface-4': '#434651',
    '--tv-text-primary': '#FFFFFF', // Pure white for maximum contrast
    '--tv-text-secondary': '#E0E0E0', // Very bright for excellent readability
    '--tv-text-tertiary': '#B0B0B0', // Bright gray for good contrast
    '--tv-text-disabled': '#434651',
    '--tv-text-inverse': '#FFFFFF',
    '--tv-border-primary': '#363A45',
    '--tv-border-secondary': '#2A2E39',
    '--tv-border-tertiary': '#434651'
  }
};

class ThemeOptimizer {
  private root: HTMLElement;
  private currentTheme: 'light' | 'dark' = 'dark';
  private isTransitioning = false;

  constructor() {
    this.root = document.documentElement;
    this.loadInitialTheme();
  }

  private loadInitialTheme(): void {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    this.currentTheme = savedTheme || 'dark';
    this.applyTheme(this.currentTheme, false);
  }

  private applyTheme(theme: 'light' | 'dark', animate: boolean = true): void {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    try {
      // Set data attribute for CSS transitions
      this.root.setAttribute('data-theme', theme);
      
      // Batch all CSS property updates - no animation delay
      this.batchUpdateProperties(THEME_CONFIG[theme]);
      
      // Save to localStorage
      localStorage.setItem('theme', theme);
      
      this.currentTheme = theme;
      
      // Reset transition flag immediately for instant response
      this.isTransitioning = false;
    } catch (error) {
      console.error('Theme application error:', error);
      this.isTransitioning = false;
    }
  }

  private batchUpdateProperties(colors: ThemeColors): void {
    // Create a single style string to minimize DOM operations
    const styleString = Object.entries(colors)
      .map(([property, value]) => `${property}: ${value}`)
      .join('; ');
    
    // Apply all properties at once
    this.root.style.cssText = this.root.style.cssText + '; ' + styleString;
  }

  public toggleTheme(): void {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.applyTheme(newTheme);
  }

  public setTheme(theme: 'light' | 'dark'): void {
    if (theme !== this.currentTheme) {
      this.applyTheme(theme);
    }
  }

  public getCurrentTheme(): 'light' | 'dark' {
    return this.currentTheme;
  }

  // Optimized method for bulk theme updates - instant execution
  public updateThemeProperties(updates: Partial<ThemeColors>): void {
    if (this.isTransitioning) return;
    
    // Apply updates immediately without requestAnimationFrame delay
    Object.entries(updates).forEach(([property, value]) => {
      if (value) {
        this.root.style.setProperty(property, value);
      }
    });
  }
}

// Create singleton instance
export const themeOptimizer = new ThemeOptimizer();

// Convenience functions
export const toggleTheme = () => themeOptimizer.toggleTheme();
export const setTheme = (theme: 'light' | 'dark') => themeOptimizer.setTheme(theme);
export const getCurrentTheme = () => themeOptimizer.getCurrentTheme();

export default themeOptimizer;
